import { Handshake } from 'lucide-react';
import { useDisclosure } from '@chakra-ui/react';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useRef, useState } from 'react';

import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Controller } from '@context';
import { DealSelectionModal } from '@components/modals';
import { lang } from '@lang';
import { customerState, loadProject, type Project, projectState, selectNavigation, selectUseTestContact, useAppDispatch, useAppSelector } from '@state';
import { patchBlueprintMetadata } from '@external';
import { useAppToast, useDesign } from '@hooks';
import { useSubscription } from '@rta/rta-blueprint-library';
import { Feature, FeatureHoookParams } from '..';

type AssociateDealFeatureParams = FeatureHoookParams;

export type AssociateDealFeatureValue = {
	DealModal: React.ReactNode;
};

export const useAssociateDealFeature = (_params: AssociateDealFeatureParams = {}): Feature<AssociateDealFeatureValue> => {
	const dispatch = useAppDispatch();
	const [controller, design] = useDesign<Controller, { options: { readonly: boolean; }}>();
	const project = useAppSelector(projectState);
	const customer = useAppSelector(customerState);
	const appNav = useAppSelector(selectNavigation);
	const useTestContact = useAppSelector(selectUseTestContact);
	const appToast = useAppToast();
	const { getToken } = useKeystoneSession();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [pending, setPending] = useState(false);
	// A ref-backed mutex: the check-and-set below is synchronous, so no second call can slip in
	// before the first sets it, unlike a state flag which only takes effect on the next render.
	const pendingRef = useRef(false);

	useSubscription(controller?.kitchen ?? null, false, [
		['kitchen_deal_id', 'updated']
	]);

	const isBlueprintController = controller instanceof BpController;
	const kitchenDealId = isBlueprintController ? controller.kitchen.dealId : '';
	const currentDealId = kitchenDealId || (project.length > 0 ? project[0].dealId : '');

	const [disabled, tooltip] = (() => {
		if (!customer.email) return [true, lang.crm.tooltip.disabledAssociateDeal];
		if (pending) return [true, lang.crm.tooltip.associateDealPending];
		return [false, lang.crm.tooltip.associateDeal];
	})();

	// Persisted by its own API write — independent of the design save — so the pairing (and the
	// deal's Blueprint Tool link) survives the designer closing the tool without saving.
	const persistDealId = async (dealId: string): Promise<[string | null, string | null]> => {
		const blueprint = design?.blueprint ?? null;
		if (!blueprint || !appNav.email) return [null, lang.crm.message.dealAssociationMissingContext];

		const [success] = await patchBlueprintMetadata({
			customerEmail: appNav.email,
			designId: blueprint.submissionId ?? blueprint.id,
			metadata: {},
			dealId,
			getToken,
			mode: 'live'
		});

		return success ? [dealId, null] : [null, lang.crm.message.dealAssociationFailed];
	};

	// Reflects dealId in memory only once persisted, and pendingRef guarantees at most one persist is ever in flight — so state can never diverge or resolve out of order.
	const applyDealId = async (dealId: string): Promise<void> => {
		if (pendingRef.current) return;
		pendingRef.current = true;
		setPending(true);

		try {
			const [persistedDealId, error] = await persistDealId(dealId);
			if (persistedDealId === null) {
				appToast.error({ title: error ?? lang.crm.message.dealAssociationFailed });
				return;
			}
			if (controller && isBlueprintController) controller.kitchen.dealId = persistedDealId;
		} catch (error) {
			console.log('applyDealId:error', error);
			appToast.error({ title: lang.crm.message.dealAssociationFailed });
		} finally {
			pendingRef.current = false;
			setPending(false);
		}
	};

	const handleDealSelect = async (dealId: string): Promise<void> => {
		await applyDealId(dealId);
		onClose();
	};

	const handleDealCreated = async (deal: Project): Promise<void> => {
		dispatch(loadProject({ email: customer.email, useTestContact }));
		await applyDealId(deal.dealId);
		onClose();
	};

	const DealModal = (
		<DealSelectionModal
			isOpen={isOpen}
			onClose={onClose}
			deals={project}
			currentDealId={currentDealId}
			onSelect={handleDealSelect}
			customer={customer}
			onDealCreated={handleDealCreated}
		/>
	);

	return {
		name: 'associateDeal',
		label: lang.crm.label.associateDeal,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <Handshake {...props} />,
		disabled,
		value: { DealModal },
		hidden: false,
		handler: () => {
			onOpen();
		}
	};
};
